import { difficultyLabel } from "~/lib/difficulty"
import { Field, FieldLabel } from "~/components/ui/field"
import { GraduationCapIcon, PlayIcon, Trash2Icon } from "lucide-react"
import { type ComponentProps, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { EmptyState, LoadingState } from "~/components/screens/StateScreen"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { EntityIdentity } from "~/components/EntityIdentity"
import { SearchableSelect } from "~/components/ui/searchable-select"
import { Button } from "~/components/ui/button"
import { Card } from "~/components/ui/card"
import { Input } from "~/components/ui/input"
import { Label } from "~/components/ui/label"

import { ScrollArea } from "~/components/ui/scroll-area"
import { Switch } from "~/components/ui/switch"
import { Textarea } from "~/components/ui/textarea"
import { useTutorial } from "~/hooks/useTutorial"
import { useTeam } from "~/hooks/useTeam"
import { authClient } from "~/lib/auth-client"
import { api, type RouterOutputs } from "~/trpc/react"

type AutomationActionDraft = { id: string; type: string; config: unknown }
type AutomationDefinitionDraft = {
	trigger: { type: string; config: unknown }
	conditionTree: Record<string, unknown> | null
	trueActions: AutomationActionDraft[]
	falseActions: AutomationActionDraft[]
}
type AutomationDraft = {
	name: string
	description: string
	enabled: boolean
	executionMode: "EveryMatch" | "OncePerIssue"
	executionOwnerUserId: string
	definition: AutomationDefinitionDraft
}

const emptyDefinition: AutomationDefinitionDraft = {
	trigger: { type: "", config: {} },
	conditionTree: null,
	trueActions: [],
	falseActions: []
}

const emptyDraft: AutomationDraft = {
	name: "New automation",
	description: "",
	enabled: false,
	executionMode: "EveryMatch",
	executionOwnerUserId: "",
	definition: emptyDefinition
}

const record = (value: unknown) =>
	value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {}

const stringList = (value: unknown) =>
	Array.isArray(value)
		? value.filter((item): item is string => typeof item === "string")
		: typeof value === "string" && value
			? value
					.split(",")
					.map((item) => item.trim())
					.filter(Boolean)
			: []

const actionDraft = (value: unknown): AutomationActionDraft => {
	const item = record(value)
	return {
		id: String(item.id ?? crypto.randomUUID()),
		type: String(item.type ?? ""),
		config: item.config ?? {}
	}
}

function AutomationSelect({
	value,
	options,
	onChange,
	placeholder = "Select..."
}: {
	value: string
	options: ComponentProps<typeof SearchableSelect>["options"]
	onChange: (value: string) => void
	placeholder?: string
}) {
	const selected =
		options.find((option) => option.value === value) ?? options[0]
	useEffect(() => {
		if (selected && value !== selected.value) onChange(selected.value)
	}, [onChange, selected, value])

	return (
		<SearchableSelect
			value={selected?.value ?? ""}
			options={options}
			onChange={onChange}
			placeholder={placeholder}
		/>
	)
}

function AutomationMultiSelect({
	value,
	options,
	onChange,
	placeholder = "Select..."
}: {
	value: string[]
	options: ComponentProps<typeof SearchableSelect>["options"]
	onChange: (value: string[]) => void
	placeholder?: string
}) {
	return (
		<SearchableSelect
			multiple
			value={value}
			options={options}
			onChange={onChange}
			placeholder={placeholder}
		/>
	)
}

function ConfigFieldsEditor({
	context,
	fields,
	onChange,
	registry,
	value
}: {
	context: RouterOutputs["automations"]["context"] | undefined
	fields: readonly unknown[]
	registry: RouterOutputs["automations"]["registry"] | undefined
	value: unknown
	onChange: (value: Record<string, unknown>) => void
}) {
	const config = record(value)
	if (!fields.length) return null
	return (
		<div className="grid gap-3">
			{fields.map((rawField) => {
				const field = record(rawField)
				const name = String(field.name ?? "")
				const type = String(field.type ?? "")
				const label = String(field.label ?? name)
				const update = (nextValue: unknown) =>
					onChange({
						...config,
						[name]:
							type === "difficulty"
								? Array.isArray(nextValue)
									? nextValue.map((value) =>
											value === "null" ? null : Number(value)
										)
									: nextValue === "null"
										? null
										: Number(nextValue)
								: nextValue
					})
				const values =
					type === "difficulty" && Array.isArray(config[name])
						? config[name].map(String)
						: stringList(config[name])
				const options = Array.isArray(field.options)
					? field.options.map(String)
					: type === "status"
						? (registry?.statuses ?? [])
						: type === "priority"
							? (registry?.priorities ?? [])
							: type === "difficulty"
								? ["null", ...(registry?.difficulties ?? [])]
								: []
				const selectOptions =
					type === "member"
						? (context?.members.map((member) => ({
								value: name === "recipientUserIds" ? member.userId : member.id,
								label: member.name ?? member.email
							})) ?? [])
						: type === "team"
							? (context?.teams.map((team) => ({
									value: team.id,
									label: team.name
								})) ?? [])
							: type === "label"
								? (context?.labels.map((label) => ({
										value: label.id,
										label: label.name
									})) ?? [])
								: name === "recipientTeamRoleIds"
									? (context?.teamRoles.map((role) => ({
											value: role.id,
											label: role.name
										})) ?? [])
									: name === "recipientOrgRoleIds"
										? (context?.orgRoles.map((role) => ({
												value: role.id,
												label: role.name
											})) ?? [])
										: options.map((option) => ({
												value: String(option),
												label:
													type === "difficulty"
														? difficultyLabel(
																option === "null" ? null : Number(option),
																context?.difficulties
															)
														: String(option)
											}))
				return (
					<div key={name} className="space-y-1.5">
						<Label>{label}</Label>
						{field.multiple && selectOptions.length ? (
							<AutomationMultiSelect
								value={values}
								options={selectOptions}
								onChange={update}
							/>
						) : selectOptions.length ? (
							<AutomationSelect
								value={
									type === "difficulty" && config[name] === null
										? "null"
										: String(config[name] ?? "")
								}
								options={selectOptions}
								onChange={update}
							/>
						) : type === "boolean" ? (
							<div className="flex items-center gap-2">
								<Switch
									checked={config[name] === true}
									onCheckedChange={update}
								/>
								<span className="text-sm text-text-sub">Enabled</span>
							</div>
						) : type === "number" || type === "durationDays" ? (
							<Input
								type="number"
								value={String(config[name] ?? "")}
								onChange={(event) =>
									update(
										event.target.value ? Number(event.target.value) : undefined
									)
								}
							/>
						) : type === "date" ? (
							<Input
								type="date"
								value={String(config[name] ?? "")}
								onChange={(event) => update(event.target.value || null)}
							/>
						) : field.multiple ? (
							<Input
								value={values.join(", ")}
								placeholder="comma-separated values"
								onChange={(event) => update(stringList(event.target.value))}
							/>
						) : type === "secretRef" ? (
							<Input
								value={String(config[name] ?? "")}
								placeholder="AUTOMATION_SECRET_NAME"
								onChange={(event) => update(event.target.value)}
							/>
						) : (
							<Input
								value={String(config[name] ?? "")}
								onChange={(event) => update(event.target.value)}
							/>
						)}
					</div>
				)
			})}
		</div>
	)
}

function ConditionTreeEditor({
	context,
	node,
	onChange,
	registry
}: {
	context: RouterOutputs["automations"]["context"] | undefined
	node: Record<string, unknown> | null
	onChange: (node: Record<string, unknown> | null) => void
	registry: RouterOutputs["automations"]["registry"] | undefined
}) {
	const conditions = (registry?.conditions ?? []).filter(
		(condition) => condition.enabled
	)
	const firstCondition = conditions[0]
	const newRule = () => ({
		type: firstCondition?.id ?? "",
		operator: firstCondition?.operators[0] ?? "",
		value: ""
	})
	const renderNode = (
		currentNode: Record<string, unknown>,
		update: (node: Record<string, unknown>) => void,
		remove: () => void,
		depth = 0
	) => {
		if (currentNode.op === "and" || currentNode.op === "or") {
			const children = Array.isArray(currentNode.children)
				? currentNode.children.map(record)
				: []
			return (
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<AutomationSelect
							value={String(currentNode.op)}
							options={[
								{ value: "and", label: "AND" },
								{ value: "or", label: "OR" }
							]}
							onChange={(op) => update({ ...currentNode, op })}
						/>
						<Button
							type="button"
							size="sm"
							variant="outline"
							disabled={!firstCondition}
							onClick={() =>
								update({ ...currentNode, children: [...children, newRule()] })
							}
						>
							Add rule
						</Button>
						<Button
							type="button"
							size="sm"
							variant="outline"
							disabled={depth >= 3 || !firstCondition}
							onClick={() =>
								update({
									...currentNode,
									children: [...children, { op: "and", children: [newRule()] }]
								})
							}
						>
							Add group
						</Button>
						<Button
							type="button"
							size="icon"
							variant="ghost"
							className="ml-auto"
							onClick={remove}
						>
							<Trash2Icon className="size-4" />
						</Button>
					</div>
					{children.map((child, index) => (
						<div key={index} className="border-l border-border pl-3">
							{renderNode(
								child,
								(nextChild) =>
									update({
										...currentNode,
										children: children.map((item, childIndex) =>
											childIndex === index ? nextChild : item
										)
									}),
								() =>
									update({
										...currentNode,
										children: children.filter(
											(_, childIndex) => childIndex !== index
										)
									}),
								depth + 1
							)}
						</div>
					))}
				</div>
			)
		}
		const selected =
			conditions.find((condition) => condition.id === currentNode.type) ??
			conditions[0]
		const operators = selected?.operators ?? []
		const operator = operators.includes(String(currentNode.operator) as never)
			? String(currentNode.operator)
			: (operators[0] ?? "")
		return (
			<div className="space-y-3">
				<div className="flex items-center gap-2">
					<AutomationSelect
						value={String(currentNode.type ?? selected?.id ?? "")}
						options={conditions.map((condition) => ({
							value: condition.id,
							label: condition.label
						}))}
						onChange={(value) => {
							const next = conditions.find(
								(condition) => condition.id === value
							)
							update({
								type: value,
								operator: next?.operators[0] ?? "",
								value: ""
							})
						}}
					/>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="ml-auto"
						onClick={remove}
					>
						<Trash2Icon className="size-4" />
					</Button>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<AutomationSelect
						value={operator}
						options={operators.map((item) => ({ value: item, label: item }))}
						onChange={(value) => update({ ...currentNode, operator: value })}
					/>
					{operator !== "empty" && operator !== "notEmpty" ? (
						<ConfigFieldsEditor
							context={context}
							registry={registry}
							fields={[
								{
									name: "value",
									label: "Value",
									type: selected?.valueType ?? "",
									multiple: [
										"in",
										"notIn",
										"intersects",
										"notIntersects"
									].includes(operator)
								}
							]}
							value={{ value: currentNode.value }}
							onChange={(config) =>
								update({ ...currentNode, value: config.value })
							}
						/>
					) : null}
				</div>
			</div>
		)
	}
	return (
		<div className="space-y-3">
			{node ? (
				renderNode(node, onChange, () => onChange(null))
			) : (
				<div className="flex gap-2 rounded-lg border border-dashed border-border p-3">
					<Button
						type="button"
						size="sm"
						variant="outline"
						disabled={!firstCondition}
						onClick={() => onChange(newRule())}
					>
						Add condition
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						disabled={!firstCondition}
						onClick={() => onChange({ op: "and", children: [newRule()] })}
					>
						Add group
					</Button>
				</div>
			)}
		</div>
	)
}

export default function AutomationEditorPage() {
	const { team, isLoading, orgSlug } = useTeam()
	const { data: session } = authClient.useSession()
	const { automationId } = useParams<{ automationId?: string }>()
	const navigate = useNavigate()
	const utils = api.useUtils()
	const ruleId = automationId ?? null
	const isNew = !ruleId
	const [draft, setDraft] = useState<AutomationDraft>(emptyDraft)
	const [savedJson, setSavedJson] = useState(JSON.stringify(emptyDraft))
	const [newDraftInitialized, setNewDraftInitialized] = useState(false)

	const registryQuery = api.automations.registry.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: Boolean(team?.id && team.automationsFeatureEnabled) }
	)
	const contextQuery = api.automations.context.useQuery(
		{ teamId: team?.id ?? "" },
		{ enabled: Boolean(team?.id && team.automationsFeatureEnabled) }
	)
	const ruleQuery = api.automations.get.useQuery(
		{ teamId: team?.id ?? "", ruleId: ruleId ?? "" },
		{ enabled: Boolean(team?.id && team.automationsFeatureEnabled && ruleId) }
	)
	const tutorial = useTutorial("automations-v1", {
		automatic: false,
		enabled: Boolean(
			team?.automationsFeatureEnabled &&
			registryQuery.isSuccess &&
			contextQuery.isSuccess &&
			(isNew ? newDraftInitialized : ruleQuery.isSuccess)
		)
	})
	const createMutation = api.automations.create.useMutation()
	const updateMutation = api.automations.update.useMutation()
	const enabledMutation = api.automations.setEnabled.useMutation()

	useEffect(() => {
		setNewDraftInitialized(false)
	}, [ruleId])

	useEffect(() => {
		if (
			!isNew ||
			!contextQuery.data ||
			!registryQuery.data ||
			!session ||
			newDraftInitialized
		)
			return
		const nextDraft = {
			...emptyDraft,
			executionOwnerUserId: session.user.id,
			definition: {
				...emptyDefinition,
				trigger: {
					type:
						registryQuery.data.triggers.find((item) => item.enabled)?.id ?? "",
					config: {}
				}
			}
		}
		setDraft(nextDraft)
		setSavedJson(JSON.stringify(nextDraft))
		setNewDraftInitialized(true)
	}, [
		contextQuery.data,
		isNew,
		newDraftInitialized,
		registryQuery.data,
		session
	])

	useEffect(() => {
		if (!ruleQuery.data || !ruleId) return
		const definition = record(ruleQuery.data.definition)
		const trigger = record(definition.trigger)
		const nextDraft: AutomationDraft = {
			name: ruleQuery.data.name,
			description: ruleQuery.data.description ?? "",
			enabled: ruleQuery.data.enabled,
			executionMode: ruleQuery.data.executionMode,
			executionOwnerUserId: ruleQuery.data.executionOwnerUserId,
			definition: {
				trigger: {
					type: String(trigger.type ?? emptyDefinition.trigger.type),
					config: trigger.config ?? {}
				},
				conditionTree: definition.conditionTree
					? record(definition.conditionTree)
					: null,
				trueActions: Array.isArray(definition.trueActions)
					? definition.trueActions.map(actionDraft)
					: [],
				falseActions: Array.isArray(definition.falseActions)
					? definition.falseActions.map(actionDraft)
					: []
			}
		}
		setDraft(nextDraft)
		setSavedJson(JSON.stringify(nextDraft))
	}, [ruleQuery.data, ruleId])

	const draftJson = JSON.stringify(draft)
	const isDirty = draftJson !== savedJson
	const registry = registryQuery.data
	const context = contextQuery.data
	const triggerCatalog = registry?.triggers ?? []
	const trigger = draft.definition.trigger
	const hasCondition = Boolean(draft.definition.conditionTree)
	const firstCondition = registry?.conditions.find((item) => item.enabled)
	const triggerIsAvailable = triggerCatalog.some(
		(item) => item.id === trigger.type && item.enabled
	)
	const hasActions =
		draft.definition.trueActions.length > 0 ||
		(hasCondition && draft.definition.falseActions.length > 0)
	const canSave =
		Boolean(draft.name.trim()) &&
		Boolean(draft.executionOwnerUserId || session?.user.id) &&
		triggerIsAvailable &&
		hasActions
	const saveDisabledReason = !draft.name.trim()
		? "Name this automation first."
		: !triggerIsAvailable
			? "Choose an available trigger."
			: !hasActions
				? "Add at least one action."
				: !draft.executionOwnerUserId && !session?.user.id
					? "Pick an execution owner."
					: null

	const addAction = (branch: "trueActions" | "falseActions") => {
		const action = registry?.actions.find((item) => item.enabled)
		if (!action) return
		setDraft((current) => ({
			...current,
			definition: {
				...current.definition,
				[branch]: [
					...current.definition[branch],
					{ id: crypto.randomUUID(), type: action.id, config: {} }
				]
			}
		}))
	}

	const actionBranches = [
		{
			branch: "trueActions" as const,
			title: hasCondition ? "Then" : "Actions",
			actions: draft.definition.trueActions
		},
		...(hasCondition
			? [
					{
						branch: "falseActions" as const,
						title: "Otherwise",
						actions: draft.definition.falseActions
					}
				]
			: [])
	]

	const save = async () => {
		if (!team) return
		if (!canSave) {
			toast.error(saveDisabledReason ?? "Automation is incomplete")
			return
		}
		const payload = {
			teamId: team.id,
			name: draft.name,
			description: draft.description || null,
			enabled: draft.enabled,
			executionMode: draft.executionMode,
			executionOwnerUserId: draft.executionOwnerUserId || session?.user.id,
			definition: {
				...draft.definition,
				falseActions: hasCondition ? draft.definition.falseActions : []
			}
		}
		if (ruleId) {
			await updateMutation.mutateAsync({ ...payload, ruleId })
			toast.success("Automation saved")
		} else {
			const created = await createMutation.mutateAsync(payload)
			toast.success("Automation created")
			if (orgSlug && team.slug) {
				navigate(`/${orgSlug}/${team.slug}/automations/${created.id}`, {
					replace: true
				})
			}
		}
		const nextDraft = {
			...draft,
			definition: {
				...draft.definition,
				falseActions: hasCondition ? draft.definition.falseActions : []
			}
		}
		setDraft(nextDraft)
		setSavedJson(JSON.stringify(nextDraft))
		await Promise.all([
			utils.automations.list.invalidate({ teamId: team.id }),
			utils.automations.get.invalidate()
		])
	}

	const resetDraft = async () => {
		const parsed = JSON.parse(savedJson)
		setDraft(parsed)
	}

	if (isLoading) return <LoadingState />

	if (!team) return <EmptyState title="Team not found." />

	if (!team.automationsFeatureEnabled) {
		return <EmptyState title="Automations unavailable" />
	}

	if (registryQuery.isLoading || contextQuery.isLoading) return <LoadingState />

	if (ruleId && ruleQuery.isLoading) return <LoadingState />

	const backHref = `/${orgSlug}/${team.slug}/automations`

	return (
		<div
			ref={tutorial.scope}
			className="flex h-full min-h-0 flex-col text-text-main"
		>
			<AppHeader
				contentClassName="flex items-center gap-2"
				actions={
					<Button
						variant="ghost"
						size="sm"
						disabled={tutorial.running}
						onClick={tutorial.start}
					>
						<GraduationCapIcon className="size-4" />
						Tutorial
					</Button>
				}
			>
				<EntityIdentity kind="team" name={team.name} image={team.logo} />
				<span className="text-text-sub">/</span>
				<Link
					to={backHref}
					className="font-medium text-text-main hover:underline"
				>
					Automations
				</Link>
				<span className="text-text-sub">/</span>
				<span className="truncate font-medium text-text-main">
					{draft.name || "Untitled automation"}
				</span>
			</AppHeader>

			<div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden xl:grid-cols-[minmax(0,1fr)_300px]">
				<ScrollArea className="min-h-0">
					<main className="mx-auto flex w-full max-w-3xl flex-col gap-3 p-4 pb-28">
						<Card density="compact" data-tutorial="automation-trigger">
							<div className="flex items-center justify-between gap-3">
								<h2 className="font-semibold">Trigger</h2>
								{!hasCondition && firstCondition ? (
									<Button
										data-tutorial="automation-condition"
										type="button"
										size="sm"
										variant="outline"
										onClick={() =>
											setDraft((current) => ({
												...current,
												definition: {
													...current.definition,
													conditionTree: {
														op: "and",
														children: [
															{
																type: firstCondition.id,
																operator: firstCondition.operators[0],
																value: ""
															}
														]
													}
												}
											}))
										}
									>
										Add condition
									</Button>
								) : null}
							</div>
							<div className="grid gap-3">
								<div className="space-y-1.5">
									<Label>When</Label>
									<AutomationSelect
										value={trigger.type}
										options={triggerCatalog
											.filter(
												(item) => item.enabled || item.id === trigger.type
											)
											.map((item) => ({
												value: item.id,
												label: item.label,
												disabled: !item.enabled
											}))}
										onChange={(value) =>
											setDraft((current) => ({
												...current,
												definition: {
													...current.definition,
													trigger: { type: value, config: {} }
												}
											}))
										}
									/>
								</div>
								<ConfigFieldsEditor
									context={context}
									registry={registry}
									fields={
										triggerCatalog.find((item) => item.id === trigger.type)
											?.configFields ?? []
									}
									value={trigger.config}
									onChange={(config) =>
										setDraft((current) => ({
											...current,
											definition: {
												...current.definition,
												trigger: { ...current.definition.trigger, config }
											}
										}))
									}
								/>
							</div>
						</Card>
						{hasCondition ? (
							<>
								<div className="mx-auto h-7 w-0.5 rounded-full bg-orbit/80" />
								<Card density="compact" data-tutorial="automation-condition">
									<div className="flex items-center justify-between gap-3">
										<h2 className="font-semibold">Condition</h2>
										<Button
											type="button"
											size="sm"
											variant="ghost"
											onClick={() =>
												setDraft((current) => ({
													...current,
													definition: {
														...current.definition,
														conditionTree: null,
														falseActions: []
													}
												}))
											}
										>
											Remove condition
										</Button>
									</div>
									<ConditionTreeEditor
										context={context}
										registry={registry}
										node={draft.definition.conditionTree}
										onChange={(conditionTree) =>
											setDraft((current) => ({
												...current,
												definition: { ...current.definition, conditionTree }
											}))
										}
									/>
								</Card>
							</>
						) : null}
						<div className="mx-auto h-7 w-0.5 rounded-full bg-orbit/80" />
						{actionBranches.map(({ branch, title, actions }) => (
							<Card
								key={branch}
								density="compact"
								data-tutorial={
									branch === "trueActions" ? "automation-actions" : undefined
								}
							>
								<div className="flex items-center justify-between gap-3">
									<h2 className="font-semibold">{title}</h2>
									<Button
										type="button"
										size="sm"
										variant="outline"
										disabled={!registry?.actions.some((item) => item.enabled)}
										onClick={() => addAction(branch)}
									>
										Add action
									</Button>
								</div>
								{actions.length ? (
									<div className="space-y-3">
										{actions.map((action, index) => (
											<Card key={action.id} variant="secondary" density="tight">
												<div className="flex items-center justify-between gap-3">
													<div className="flex items-center gap-2">
														<span className="flex size-6 items-center justify-center rounded-full bg-background-tertiary text-xs text-text-sub">
															{index + 1}
														</span>
														<span className="text-xs font-semibold uppercase tracking-wide text-text-sub">
															{title} action
														</span>
													</div>
													<Button
														type="button"
														size="icon"
														variant="ghost"
														onClick={() =>
															setDraft((current) => ({
																...current,
																definition: {
																	...current.definition,
																	[branch]: current.definition[branch].filter(
																		(item) => item.id !== action.id
																	)
																}
															}))
														}
													>
														<Trash2Icon className="size-4" />
													</Button>
												</div>
												<AutomationSelect
													value={action.type}
													options={(registry?.actions ?? [])
														.filter(
															(item) => item.enabled || item.id === action.type
														)
														.map((item) => ({
															value: item.id,
															label: item.label,
															tooltip: item.description,
															disabled: !item.enabled
														}))}
													onChange={(type) =>
														setDraft((current) => ({
															...current,
															definition: {
																...current.definition,
																[branch]: current.definition[branch].map(
																	(item) =>
																		item.id === action.id
																			? { ...item, type, config: {} }
																			: item
																)
															}
														}))
													}
												/>
												<ConfigFieldsEditor
													context={context}
													registry={registry}
													fields={
														registry?.actions.find(
															(item) => item.id === action.type
														)?.configFields ?? []
													}
													value={action.config}
													onChange={(config) =>
														setDraft((current) => ({
															...current,
															definition: {
																...current.definition,
																[branch]: current.definition[branch].map(
																	(item) =>
																		item.id === action.id
																			? { ...item, config }
																			: item
																)
															}
														}))
													}
												/>
											</Card>
										))}
									</div>
								) : (
									<p className="text-sm text-text-sub">No actions yet.</p>
								)}
							</Card>
						))}
					</main>
				</ScrollArea>
				<aside className="min-h-0 border-t border-border bg-background-secondary/30 xl:border-l xl:border-t-0">
					<ScrollArea className="h-full">
						<div className="space-y-5 p-4 pb-28">
							<section className="space-y-3">
								<h2 className="font-semibold">Settings</h2>
								<div className="grid gap-3">
									<div
										className="flex items-center justify-between"
										data-tutorial="automation-enabled"
									>
										<Label htmlFor="automation-enabled">Enabled</Label>
										<Switch
											id="automation-enabled"
											checked={draft.enabled}
											onCheckedChange={async (checked) => {
												setDraft({ ...draft, enabled: checked })
												if (!ruleId) return
												await enabledMutation.mutateAsync({
													teamId: team.id,
													ruleId,
													enabled: checked
												})
												await utils.automations.list.invalidate({
													teamId: team.id
												})
											}}
										/>
									</div>
									<Field data-tutorial="automation-name">
										<FieldLabel>Name</FieldLabel>
										<Input
											value={draft.name}
											onChange={(event) =>
												setDraft({ ...draft, name: event.target.value })
											}
										/>
									</Field>
									<Field>
										<FieldLabel>Description</FieldLabel>
										<Textarea
											value={draft.description}
											onChange={(event) =>
												setDraft({ ...draft, description: event.target.value })
											}
										/>
									</Field>
									<div
										className="space-y-1.5"
										data-tutorial="automation-frequency"
									>
										<Label>Execution mode</Label>
										<AutomationSelect
											value={draft.executionMode}
											options={[
												{ value: "EveryMatch", label: "Every match" },
												{ value: "OncePerIssue", label: "Once per issue" }
											]}
											onChange={(value) =>
												setDraft({
													...draft,
													executionMode:
														value === "OncePerIssue"
															? "OncePerIssue"
															: "EveryMatch"
												})
											}
										/>
									</div>
									<div className="space-y-1.5" data-tutorial="automation-owner">
										<Label>Owner</Label>
										<AutomationSelect
											value={draft.executionOwnerUserId}
											options={
												context?.members.map((member) => ({
													value: member.userId,
													label: member.name ?? member.email
												})) ?? []
											}
											onChange={(executionOwnerUserId) =>
												setDraft({ ...draft, executionOwnerUserId })
											}
										/>
									</div>
								</div>
							</section>

							{ruleId ? (
								<section className="space-y-3 border-t border-border pt-4">
									<h2 className="font-semibold">Preview and runs</h2>
									<Button asChild variant="outline" className="w-full">
										<Link to={`${backHref}/${ruleId}/runs`}>
											<PlayIcon className="size-4" /> Open runs
										</Link>
									</Button>
								</section>
							) : null}
						</div>
					</ScrollArea>
				</aside>
			</div>

			<UnsavedChanges
				isVisible={isDirty}
				canSave={
					canSave && !createMutation.isPending && !updateMutation.isPending
				}
				onSave={save}
				onReset={resetDraft}
			/>
		</div>
	)
}
